// Layout INBOX (M11 polish): lista de cobranças à esquerda, preview do
// batch selecionado à direita. Click na lista abre preview inline sem sair
// da página. Botão "Abrir completa" vai pra BatchDetailPage full-screen
// com todas as ações (reforçar, excluir, editar, refund, etc).
// Mobile (<md): quando batch selecionado, esconde lista e mostra só preview.
import React, { useCallback, useEffect, useState } from 'react';
import { Card, Tag, Button, Typography, Row, Col, Space, Input, Select, message, Empty, Statistic, Table, Modal, Form } from 'antd';
import { PlusOutlined, ArrowLeftOutlined, SearchOutlined, ReloadOutlined, ExpandOutlined, FileTextOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../../../services/api';
import dayjs from 'dayjs';
import { getActiveTeamId } from '../../../../utils/activeTeam';

const { Text, Title } = Typography;
// Traduzir termos técnicos do status da charge pra copy pt-BR amigável.
const CHARGE_STATUS_LABEL = {
  pending: { color: 'orange', label: 'Aguardando' },
  paid: { color: 'green', label: 'Paga' },
  refunded: { color: 'red', label: 'Estornada' },
  cancelled: { color: 'default', label: 'Cancelada' },
  error: { color: 'red', label: 'Com erro' },
  overdue: { color: 'red', label: 'Vencida' },
};
const PRIMARY = '#128C7E';
const fmtBRL = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

const STATUS_META = {
  sent: { color: 'green', label: 'Enviado' },
  scheduled: { color: 'blue', label: 'Agendado' },
  sending: { color: 'processing', label: 'Enviando' },
  partial_error: { color: 'orange', label: 'Parcial' },
  error: { color: 'red', label: 'Erro' },
};

export default function AllBatchesPage() {
  const nav = useNavigate();
  const teamId = getActiveTeamId();
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [pagination] = useState({ page: 1, limit: 50 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Edição rápida dos dados da cobrança (nome + descrição) direto do preview,
  // sem precisar abrir a página completa. Mesmo endpoint PUT do BatchDetail.
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm] = Form.useForm();
  const openEdit = () => {
    if (!detail?.batch) return;
    editForm.setFieldsValue({
      name: detail.batch.name || '',
      description: (detail.charges?.find(c => ['pending', 'error'].includes(c.status)) || detail.charges?.[0])?.description || '',
    });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    try {
      const v = await editForm.validateFields();
      setEditSaving(true);
      await api.put(`/community/finance/charges/batches/${selectedId}`, {
        team_id: teamId, name: v.name, description: v.description,
      });
      message.success('Cobrança atualizada');
      setEditOpen(false);
      // reload do detail pra refletir mudanças
      const r = await api.get(`/community/finance/charges/batches/${selectedId}`, { params: { team_id: teamId } });
      setDetail(r.data);
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.response?.data?.message || 'Falhou atualizar');
    } finally { setEditSaving(false); }
  };

  const load = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const params = { team_id: teamId, page: pagination.page, limit: pagination.limit };
      if (statusFilter) params.status = statusFilter;
      const r = await api.get('/community/finance/charges/batches', { params });
      setData(r.data);
    } catch (e) {
      message.error(e.response?.data?.message || 'Falhou carregar');
    } finally { setLoading(false); }
  }, [teamId, pagination.page, pagination.limit, statusFilter]);
  useEffect(() => { load(); }, [load]);

  // Auto-seleciona o primeiro em desktop na primeira carga (não em cada
  // mudança de selectedId — se botasse aqui, ao clicar item lista recarregaria).
  useEffect(() => {
    if (!selectedId && data.items?.length > 0 && window.innerWidth >= 768) {
      setSelectedId(data.items[0].id);
    }
  }, [data.items, selectedId]);

  const loadDetail = useCallback(async () => {
    if (!selectedId || !teamId) { setDetail(null); return; }
    setDetailLoading(true);
    try {
      const r = await api.get(`/community/finance/charges/batches/${selectedId}`, { params: { team_id: teamId } });
      setDetail(r.data);
    } catch (e) {
      setDetail(null);
    } finally { setDetailLoading(false); }
  }, [selectedId, teamId]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const filteredItems = search
    ? data.items.filter(b => b.name?.toLowerCase().includes(search.toLowerCase()))
    : data.items;

  const isMobileDetailView = selectedId && typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div style={{ padding: '16px 12px', maxWidth: 1400, margin: '0 auto' }}>
      <Row justify="space-between" align="middle" wrap gutter={[8, 8]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Button size="small" icon={<ArrowLeftOutlined />}
            onClick={() => isMobileDetailView ? setSelectedId(null) : nav('/comunidade/financeiro')}>
            {isMobileDetailView ? 'Voltar à lista' : 'Voltar'}
          </Button>
          <Title level={4} style={{ marginTop: 8, marginBottom: 4 }}>Todas as cobranças</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>{data.total} cobrança(s) cadastrada(s)</Text>
        </Col>
        <Col xs={24} md={12} style={{ textAlign: 'right' }}>
          <Button type="primary" icon={<PlusOutlined />}
            style={{ background: PRIMARY, borderColor: PRIMARY }}
            onClick={() => nav('/comunidade/financeiro/nova-cobranca')}>
            Nova cobrança
          </Button>
        </Col>
      </Row>

      <Row gutter={[12, 12]}>
        {/* LISTA — esconde em mobile quando preview aberto */}
        <Col xs={isMobileDetailView ? 0 : 24} md={10} lg={8}>
          <Card
            styles={{ header: { padding: '10px 12px' }, body: { padding: 8, maxHeight: 'calc(100vh - 260px)', overflowY: 'auto' } }}
            title={
              <Space wrap size={6}>
                <Input
                  placeholder="Buscar…"
                  prefix={<SearchOutlined />}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  allowClear
                  size="small"
                  style={{ width: 140 }}
                />
                <Select
                  allowClear
                  placeholder="Status"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  size="small"
                  style={{ width: 120 }}
                  options={Object.entries(STATUS_META).map(([k, v]) => ({ value: k, label: v.label }))}
                />
                <Button size="small" icon={<ReloadOutlined />} onClick={load} />
              </Space>
            }
          >
            {loading ? (
              <div style={{ textAlign: 'center', padding: 24 }}><Text type="secondary">Carregando…</Text></div>
            ) : filteredItems.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhuma cobrança" />
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size={6}>
                {filteredItems.map(b => {
                  const paid = Number(b.paid_count || 0);
                  const total = Number(b.total_recipients || 0);
                  const pct = total > 0 ? Math.round((paid / total) * 100) : 0;
                  const status = STATUS_META[b.status] || { color: 'default', label: b.status };
                  const selected = b.id === selectedId;
                  return (
                    <div key={b.id}
                      onClick={() => setSelectedId(b.id)}
                      style={{
                        padding: 10,
                        borderRadius: 6,
                        cursor: 'pointer',
                        background: selected ? '#f0faf7' : '#fff',
                        borderLeft: `3px solid ${selected ? PRIMARY : 'transparent'}`,
                        transition: 'all 0.1s',
                      }}
                      onMouseEnter={e => { if (!selected) e.currentTarget.style.background = '#fafafa'; }}
                      onMouseLeave={e => { if (!selected) e.currentTarget.style.background = '#fff'; }}
                    >
                      <Row justify="space-between" align="top" gutter={[6, 2]}>
                        <Col flex="1" style={{ minWidth: 0 }}>
                          <Text strong style={{ fontSize: 13, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</Text>
                          <Text type="secondary" style={{ fontSize: 10 }}>{dayjs(b.created_at).format('DD/MM/YYYY')}</Text>
                        </Col>
                        <Col>
                          <Tag color={status.color} style={{ margin: 0, fontSize: 10 }}>{status.label}</Tag>
                        </Col>
                      </Row>
                      <div style={{ marginTop: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          <Text strong style={{ color: PRIMARY, fontSize: 11 }}>{paid}/{total}</Text> · {pct}%
                        </Text>
                        <Text strong style={{ fontSize: 11, color: PRIMARY }}>{fmtBRL(b.paid_amount_brl || 0)}</Text>
                      </div>
                    </div>
                  );
                })}
              </Space>
            )}
          </Card>
        </Col>

        {/* DETALHE — sempre visível em desktop, mobile só quando selecionado */}
        <Col xs={isMobileDetailView ? 24 : 0} md={14} lg={16}>
          {!selectedId ? (
            <Card>
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Clica numa cobrança da lista pra ver os detalhes" />
            </Card>
          ) : detailLoading ? (
            <Card><div style={{ textAlign: 'center', padding: 40 }}><Text type="secondary">Carregando…</Text></div></Card>
          ) : !detail ? (
            <Card><Text type="danger">Não deu pra carregar a cobrança.</Text></Card>
          ) : (
            <Card
              styles={{ header: { padding: '12px 16px' }, body: { padding: 12 } }}
              title={
                <Space wrap>
                  <FileTextOutlined />
                  <Text strong>{detail.batch.name}</Text>
                  <Tag color={STATUS_META[detail.batch.status]?.color || 'default'}>
                    {STATUS_META[detail.batch.status]?.label || detail.batch.status}
                  </Tag>
                </Space>
              }
              extra={
                <Space size={6}>
                  <Button size="small" icon={<EditOutlined />} onClick={openEdit}>
                    Editar
                  </Button>
                  <Button type="primary" size="small" icon={<ExpandOutlined />}
                    style={{ background: PRIMARY, borderColor: PRIMARY }}
                    onClick={() => nav(`/comunidade/financeiro/lote/${selectedId}`)}>
                    Ver detalhes
                  </Button>
                </Space>
              }
            >
              <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
                <Col xs={12} md={6}>
                  <Card size="small" styles={{ body: { padding: 10 } }}>
                    <Statistic title="Total" value={detail.summary.total} valueStyle={{ fontSize: 18 }} />
                  </Card>
                </Col>
                <Col xs={12} md={6}>
                  <Card size="small" styles={{ body: { padding: 10 } }}>
                    <Statistic title="Pagas" value={detail.summary.paid} valueStyle={{ color: PRIMARY, fontSize: 18 }} />
                    <Text type="secondary" style={{ fontSize: 10 }}>{fmtBRL(detail.summary.total_paid_amount_brl)}</Text>
                  </Card>
                </Col>
                <Col xs={12} md={6}>
                  <Card size="small" styles={{ body: { padding: 10 } }}>
                    <Statistic title="Pendentes" value={detail.summary.pending} valueStyle={{ color: '#faad14', fontSize: 18 }} />
                    <Text type="secondary" style={{ fontSize: 10 }}>{fmtBRL(detail.summary.total_pending_amount_brl)}</Text>
                  </Card>
                </Col>
                <Col xs={12} md={6}>
                  <Card size="small" styles={{ body: { padding: 10 } }}>
                    <Statistic title="Vencidas" value={detail.summary.overdue} valueStyle={{ color: '#dc2626', fontSize: 18 }} />
                  </Card>
                </Col>
              </Row>
              <div style={{ overflowX: 'auto' }}>
                <Table
                  rowKey="id"
                  size="small"
                  dataSource={detail.charges}
                  scroll={{ x: 500 }}
                  pagination={{ pageSize: 10, simple: true, size: 'small' }}
                  columns={[
                    { title: 'Nome', dataIndex: 'recipient_name', width: 140,
                      render: v => <Text style={{ whiteSpace: 'nowrap' }}>{v}</Text> },
                    { title: 'Valor', dataIndex: 'amount_brl', width: 80, align: 'right',
                      render: fmtBRL },
                    { title: 'Situação', dataIndex: 'status', width: 100, render: v => {
                      const s = CHARGE_STATUS_LABEL[v] || { color: 'default', label: v };
                      return <Tag color={s.color}>{s.label}</Tag>;
                    } },
                    { title: 'Recebido', key: 'net', width: 100, align: 'right', render: (_, r) => {
                      if (r.status !== 'paid') return '—';
                      const net = Number(r.net_to_organizer_brl || 0);
                      const release = r.expected_release_at ? dayjs(r.expected_release_at) : null;
                      const isFuture = release && release.isAfter(dayjs());
                      return (
                        <div style={{ whiteSpace: 'nowrap' }}>
                          <Text strong style={{ color: PRIMARY }}>{fmtBRL(net)}</Text>
                          {release && (
                            <div><Text style={{ fontSize: 9, color: isFuture ? '#faad14' : PRIMARY }}>
                              {isFuture ? `Cai ${release.format('DD/MM')}` : '✓'}
                            </Text></div>
                          )}
                        </div>
                      );
                    } },
                  ]}
                />
              </div>
            </Card>
          )}
        </Col>
      </Row>

      {/* Editar dados da cobrança (nome + descrição) — mesmo endpoint do
          BatchDetail, mas acessível direto do preview lateral. */}
      <Modal
        open={editOpen}
        title="Editar cobrança"
        onCancel={() => setEditOpen(false)}
        onOk={saveEdit}
        confirmLoading={editSaving}
        okText="Salvar"
        cancelText="Cancelar"
        okButtonProps={{ style: { background: PRIMARY, borderColor: PRIMARY } }}
        destroyOnClose
        width={520}
      >
        <Form form={editForm} layout="vertical" preserve={false}>
          <Form.Item label="Nome do lote" name="name"
            rules={[{ required: true, message: 'Coloca um nome' }, { max: 200 }]}>
            <Input placeholder="Ex: Mensalidade agosto" />
          </Form.Item>
          <Form.Item label="Descrição (opcional)" name="description"
            rules={[{ max: 500 }]}
            extra="Aparece pra quem recebe a cobrança. Alterar aqui atualiza só cobranças pendentes — pagas ficam com o texto original.">
            <Input.TextArea rows={3} maxLength={500} showCount
              placeholder="Ex: Mensalidade referente ao mês de agosto" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
